<template>
  <BittsSelect
    aria-label="Select role"
    :model-value="modelValue"
    :options="rolePickerOptions"
    :searchable="false"
    :use-disabled-options="useDisabledOptions"
    :disabled="disabled"
    :size="size"
    :dropdown-match-select-width="dropdownMatchSelectWidth"
    dropdown-class-name="report-role-picker__dropdown"
    class="report-role-picker"
    data-testid="report-role-picker"
    v-bind="$attrs"
    @update:model-value="
      (val: RolePickerValue) => $emit('update:modelValue', val)
    "
  >
    <template #option="{ option }">
      <div class="flex flex-col">
        <div
          :class="
            option.value === REMOVE_USER_VALUE
              ? 'text-danger-accent'
              : 'text-neutral-text-strong'
          "
        >
          {{ option.label }}
        </div>
        <div v-if="option.description" class="report-role-picker__description">
          {{ option.description }}
        </div>
      </div>
    </template>
  </BittsSelect>
</template>

<script setup lang="ts">
import { BittsSelect } from '@crossbeam/bitts';

import { computed } from 'vue';

import type { ReportAccessLevel } from '@/types/report_team_access';

type RolePickerValue = ReportAccessLevel | 'remove_user';

const props = withDefaults(
  defineProps<{
    modelValue: RolePickerValue | null;
    eligiblePermissions: ReportAccessLevel[];
    showRemove?: boolean;
    disabled?: boolean;
    useDisabledOptions?: boolean;
    dropdownMatchSelectWidth?: boolean;
    size?: 'small' | 'medium' | 'large';
  }>(),
  {
    showRemove: true,
    disabled: false,
    useDisabledOptions: false,
    dropdownMatchSelectWidth: true,
    size: 'medium',
  },
);

defineEmits<(e: 'update:modelValue', value: RolePickerValue) => void>();

const REMOVE_USER_VALUE = 'remove_user';

type RoleOption = {
  value: RolePickerValue;
  label: string;
  description?: string;
  disabled?: boolean;
  key: string;
};

const rolePickerOptions = computed<RoleOption[]>(() => {
  const options: RoleOption[] = [];
  const selectable = new Set(props.eligiblePermissions);
  const current =
    props.modelValue != null && props.modelValue !== REMOVE_USER_VALUE
      ? props.modelValue
      : null;
  const visible = new Set([
    ...props.eligiblePermissions,
    ...(current ? [current] : []),
  ]);

  if (visible.has('manager')) {
    options.push({
      value: 'manager',
      label: 'Manager',
      description: 'Create, edit, delete, add Notes, and share Lists',
      disabled: props.useDisabledOptions && !selectable.has('manager'),
      key: 'manager',
    });
  }

  if (visible.has('editor')) {
    options.push({
      value: 'editor',
      label: 'Editor',
      description: 'Create, edit, delete, and add Notes',
      disabled: props.useDisabledOptions && !selectable.has('editor'),
      key: 'editor',
    });
  }

  if (visible.has('commenter')) {
    options.push({
      value: 'commenter',
      label: 'Commenter',
      description: 'View Lists and add Notes',
      disabled: props.useDisabledOptions && !selectable.has('commenter'),
      key: 'commenter',
    });
  }

  if (visible.has('viewer')) {
    options.push({
      value: 'viewer',
      label: 'Viewer',
      description: 'View Lists',
      disabled: props.useDisabledOptions && !selectable.has('viewer'),
      key: 'viewer',
    });
  }

  if (props.showRemove) {
    options.push({
      value: REMOVE_USER_VALUE,
      label: 'Remove',
      key: REMOVE_USER_VALUE,
    });
  }

  return options;
});
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
.report-role-picker :deep(.ant-select-selector) {
  max-height: 32px;
}

.report-role-picker :deep(.ant-select-selection-item) {
  @apply text-neutral-text-strong;
}

.report-role-picker
  :deep(.ant-select-selection-item .report-role-picker__description) {
  @apply hidden;
}

.report-role-picker :deep(.ant-select-selection-item .text-wrap) {
  @apply hidden;
}
</style>

<style lang="pcss">
@reference "../../css/theme.css";
.report-role-picker__dropdown {
  @apply px-4;
  max-width: 250px;
}

.report-role-picker__dropdown .report-role-picker__description {
  @apply text-neutral-text-weak text-wrap mr-8;
}
</style>
